// Header.tsx
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "next-i18next";
import Image from "next/image";
import { useRouter } from "next/router";
import {
  LanguageOutlined,
  MenuRounded,
  KeyboardArrowDown,
} from "@mui/icons-material";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import HeaderMobile from "./HeaderMobile";
import SocialLinks from "@/components/General/SocialLinks";
import { parseJsonBody } from "@/lib/general.utils";
import useGeneralStore from "@/store/General/useGeneralStore";

const Header: React.FC = () => {
  const { t, i18n } = useTranslation("common");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showCompactHeader, setShowCompactHeader] = useState(false);
  const [servicesHover, setServicesHover] = useState(false);

  const router = useRouter();
  const lang = i18n.language;
  const isAr = lang === "ar";

  const { ServicesComponent: servicesData, fetchServicesComponent } =
    useGeneralStore((state) => ({
      ServicesComponent: state.ServicesComponent,
      fetchServicesComponent: state.fetchServicesComponent,
    }));

  useEffect(() => {
    fetchServicesComponent();
  }, [fetchServicesComponent]);

  const services = useMemo(() => {
    const servicesJson = parseJsonBody(servicesData?.jsonBody);
    return servicesJson?.services || [];
  }, [servicesData?.jsonBody]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setShowCompactHeader(scrollTop > 100); // Show compact header after scrolling past original
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleLanguageHandler = (lang: string) => {
    i18n.changeLanguage(lang);
    router.push(
      {
        pathname: router.pathname,
        query: router.query,
      },
      undefined,
      { locale: lang },
    );
  };

  return (
    <>
      {/* Original Header - Sticky */}
      <div
        style={{ direction: isAr ? "rtl" : "ltr" }}
        className="sticky top-0 z-50 w-full bg-primary border-b shadow-sm"
      >
        <div className="w-full ">
          <div className=" mx-auto px-[2rem] py-[1.5rem] h-full">
            <div className="flex items-center justify-between h-full gap-[2rem]">
              {/* Left Side - Search and Social Links */}
              <div className="hidden lg:flex items-center gap-[1rem]">
                <button
                  type="button"
                  onClick={() => toggleLanguageHandler(isAr ? "en" : "ar")}
                  aria-label={isAr ? "Switch to English" : "التبديل إلى العربية"}
                  className="flex items-center gap-2 px-4 py-2 text-white border border-white/40 rounded-full hover:bg-white/20 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
                >
                  <LanguageOutlined fontSize="small" />
                  <span className="font-medium">{isAr ? "En" : "عربي"}</span>
                </button>
                <div className="w-[0.1rem] h-[2rem] bg-white/30"></div>
                <SocialLinks />
              </div>

              {/* Center Navigation */}
              <nav className="hidden lg:flex items-center gap-[2rem] flex-1 justify-center">
                <Link
                  href="/"
                  className={`text-white hover:text-secondary font-medium text-[1rem] transition-colors ${
                    router.pathname === "/" ? "text-secondary underline" : ""
                  }`}
                >
                  {t("header.nav.home")}
                </Link>
                <Link
                  href="/about"
                  className={`text-white hover:text-secondary font-medium text-[1rem] transition-colors ${
                    router.pathname === "/about"
                      ? "text-secondary underline"
                      : ""
                  }`}
                >
                  {t("header.nav.about")}
                </Link>
                <Link
                  href="/events"
                  className={`text-white hover:text-secondary font-medium text-[1rem] transition-colors ${
                    router.pathname === "/events"
                      ? "text-secondary underline"
                      : ""
                  }`}
                >
                  {t("header.nav.events")}
                </Link>
                <Link
                  href="/news"
                  className={`text-white hover:text-secondary font-medium text-[1rem] transition-colors ${
                    router.pathname === "/news"
                      ? "text-secondary underline"
                      : ""
                  }`}
                >
                  {t("header.nav.news")}
                </Link>
                {/* <Link
                  href="/blogs"
                  className={`text-white hover:text-secondary font-medium text-[1rem] transition-colors ${
                    router.pathname === "/blogs" ||
                    router.pathname.startsWith("/blogs/")
                      ? "text-secondary underline"
                      : ""
                  }`}
                >
                  {t("blogs.title")}
                </Link> */}
                {/* Services Dropdown */}
                <div
                  className="relative "
                  onMouseEnter={() => setServicesHover(true)}
                  onMouseLeave={() => setServicesHover(false)}
                >
                  <Link
                    href="/services"
                    className={`text-white hover:text-secondary font-medium text-[1rem] transition-colors flex items-center gap-[0.5rem] ${
                      router.pathname === "/services"
                        ? "text-secondary underline"
                        : ""
                    }`}
                  >
                    {t("header.nav.services")}
                    <KeyboardArrowDown
                      className={`transition-transform ${
                        servicesHover ? "rotate-180" : ""
                      }`}
                    />
                  </Link>
                  <AnimatePresence>
                    {servicesHover && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 mt-[0.3rem] z-50"
                      >
                        <div className="bg-white rounded-lg shadow-lg min-w-[15rem] max-h-[20rem] overflow-y-auto">
                          {services.map((service: any) => (
                            <Link
                              key={service.id}
                              href={`/services/${service.slug}`}
                              className="block px-[1.5rem] py-[0.75rem] text-primary hover:bg-secondary/10 transition-colors"
                            >
                              {service.title}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                {/* <Link
                  href="/photos"
                  className={`text-white hover:text-secondary font-medium text-[1rem] transition-colors ${
                    router.pathname === "/photos"
                      ? "text-secondary underline"
                      : ""
                  }`}
                >
                  {t("header.nav.photos")}
                </Link> */}
                {/* <Link
                  href="/videos"
                  className={`text-white hover:text-secondary font-medium text-[1rem] transition-colors ${
                    router.pathname === "/videos"
                      ? "text-secondary underline"
                      : ""
                  }`}
                >
                  {t("header.nav.videos")}
                </Link> */}
                <Link
                  href="/contact"
                  className={`text-white hover:text-secondary font-medium text-[1rem] transition-colors ${
                    router.pathname === "/contact"
                      ? "text-secondary underline"
                      : ""
                  }`}
                >
                  {t("header.nav.contact")}
                </Link>
                <Link
                  href="/donate"
                  className={`text-white hover:text-secondary font-medium text-[1rem] transition-colors  px-[1rem] py-[0.5rem] rounded-lg ${
                    router.pathname === "/donate"
                      ? "!text-secondary underline"
                      : ""
                  }`}
                >
                  {t("header.nav.donate")}
                </Link>
                <Link
                  href="/volunteer"
                  className="text-white  font-medium text-[1rem] transition-colors bg-secondary hover:bg-secondary/80 px-[1rem] py-[0.5rem] rounded-lg"
                >
                  {t("header.nav.volunteer")}
                </Link>
              </nav>

              {/* Right Side - Logo and Language */}
              <div className="flex items-center justify-between gap-[1rem] w-full lg:w-auto">
                {/* Mobile Menu */}
                <div className="lg:hidden">
                  <button
                    onClick={() => setIsMobileMenuOpen(true)}
                    className="p-2 text-white"
                  >
                    <MenuRounded fontSize="large" />
                  </button>
                </div>

                {/* Logo */}
                <div className="flex items-center rounded-md bg-white aspect-square w-[5rem] md:w-[7rem]">
                  <Link href="/">
                    <Image
                      src="/icons/logo.png"
                      alt="Logo"
                      width={1000}
                      height={1000}
                      className=" object-contain"
                    />
                  </Link>
                </div>

                {/* Language Toggle */}
              </div>
            </div>
          </div>
        </div>

        <HeaderMobile open={isMobileMenuOpen} setOpen={setIsMobileMenuOpen} />
      </div>

      {/* Compact Sticky Header */}
      <AnimatePresence>
        {showCompactHeader && (
          <motion.div
            style={{ direction: isAr ? "rtl" : "ltr" }}
            className="fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-sm border-b shadow-lg"
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -80, opacity: 0 }}
            transition={{
              type: "tween",
              ease: "easeOut",
              duration: 0.2,
            }}
          >
            <div className="w-full py-4">
              <div className=" mx-auto px-8 h-full">
                <div className="flex items-center justify-between flex-row-reverse w-full h-full gap-[1rem]">
                  {/* Compact Logo */}
                  <div className="flex items-center rounded-md bg-white !h-[4rem] w-[4rem]">
                    <Link href="/">
                      <Image
                        src="/icons/logo.png"
                        alt="Logo"
                        width={100}
                        height={40}
                        className="!h-[4rem] object-contain"
                      />
                    </Link>
                  </div>

                  {/* Compact Navigation (same as main) */}
                  <nav className="hidden lg:flex items-center gap-[1.5rem] flex-1 justify-center">
                    <Link
                      href="/"
                      className={`text-white hover:text-secondary font-medium text-[1rem] transition-colors ${
                        router.pathname === "/"
                          ? "text-secondary underline"
                          : ""
                      }`}
                    >
                      {t("header.nav.home")}
                    </Link>
                    <Link
                      href="/about"
                      className={`text-white hover:text-secondary font-medium text-[1rem] transition-colors ${
                        router.pathname === "/about"
                          ? "text-secondary underline"
                          : ""
                      }`}
                    >
                      {t("header.nav.about")}
                    </Link>
                    <Link
                      href="/events"
                      className={`text-white hover:text-secondary font-medium text-[1rem] transition-colors ${
                        router.pathname === "/events"
                          ? "text-secondary underline"
                          : ""
                      }`}
                    >
                      {t("header.nav.events")}
                    </Link>
                    <Link
                      href="/news"
                      className={`text-white hover:text-secondary font-medium text-[1rem] transition-colors ${
                        router.pathname === "/news"
                          ? "text-secondary underline"
                          : ""
                      }`}
                    >
                      {t("header.nav.news")}
                    </Link>
                    <Link
                      href="/blogs"
                      className={`text-white hover:text-secondary font-medium text-[1rem] transition-colors ${
                        router.pathname === "/blogs" ||
                        router.pathname.startsWith("/blogs/")
                          ? "text-secondary underline"
                          : ""
                      }`}
                    >
                      {t("blogs.title")}
                    </Link>
                    <div
                      className="relative"
                      onMouseEnter={() => setServicesHover(true)}
                      onMouseLeave={() => setServicesHover(false)}
                    >
                      <Link
                        href="/services"
                        className={`text-white hover:text-secondary font-medium text-[1rem] transition-colors flex items-center gap-[0.5rem] ${
                          router.pathname === "/services"
                            ? "text-secondary underline"
                            : ""
                        }`}
                      >
                        {t("header.nav.services")}
                        <KeyboardArrowDown
                          className={`transition-transform ${
                            servicesHover ? "rotate-180" : ""
                          }`}
                        />
                      </Link>
                      <AnimatePresence>
                        {servicesHover && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.15 }}
                            className="absolute top-full left-0 mt-[0.3rem] z-50"
                          >
                            <div className="bg-white rounded-lg shadow-lg min-w-[15rem] max-h-[20rem] overflow-y-auto">
                              {services.map((service: any) => (
                                <Link
                                  key={service.id}
                                  href={`/services/${service.slug}`}
                                  className="block px-[1.5rem] py-[0.75rem] text-primary hover:bg-secondary/10 transition-colors"
                                >
                                  {service.title}
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    <Link
                      href="/contact"
                      className={`text-white hover:text-secondary font-medium text-[1rem] transition-colors ${
                        router.pathname === "/contact"
                          ? "text-secondary underline"
                          : ""
                      }`}
                    >
                      {t("header.nav.contact")}
                    </Link>
                    <Link
                      href="/donate"
                      className={`text-white hover:text-secondary font-medium text-[1rem] transition-colors  px-[1rem] py-[0.5rem] rounded-lg ${
                        router.pathname === "/donate"
                          ? "!text-secondary underline"
                          : ""
                      }`}
                    >
                      {t("header.nav.donate")}
                    </Link>
                    <Link
                      href="/volunteer"
                      className="text-white  font-medium text-[1rem] transition-colors bg-secondary hover:bg-secondary/80 px-[1rem] py-[0.5rem] rounded-lg"
                    >
                      {t("header.nav.volunteer")}
                    </Link>
                  </nav>

                  {/* Compact Language Toggle */}
                  <div className="hidden lg:flex items-center">
                    <button
                      onClick={() => toggleLanguageHandler(isAr ? "en" : "ar")}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-white rounded-full hover:bg-white/20 hover:text-primary transition-colors text-sm"
                    >
                      <LanguageOutlined
                        fontSize="small"
                        className="text-white"
                      />
                      <span className="text-white font-medium">
                        {isAr ? "En" : "عربي"}
                      </span>
                    </button>
                  </div>

                  {/* Mobile Menu */}
                  <div className="lg:hidden">
                    <button
                      onClick={() => setIsMobileMenuOpen(true)}
                      className="p-1 text-white"
                    >
                      <MenuRounded fontSize="medium" />
                    </button>
                  </div>

                  {/* Left - Social Links */}
                  <div className="hidden lg:flex items-center">
                    <SocialLinks />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
